import React from 'react';
import { fmtQty, perColloOf } from '../../class/util';

// Tabella risultati condivisa dalle 3 modalità della Consultazione (per articolo / per
// posizione / tutto il magazzino).
//
// `columns` elenca SOLO le colonne di contesto da mostrare: le tre colonne quantità e il
// bottone Storico ci sono sempre. Ogni modalità nasconde le colonne che ha già fissato nei
// propri filtri (sarebbero costanti su tutte le righe e ruberebbero larghezza), così sul
// tablet ET45 non si superano mai ~7 colonne.
//
//   <StockResultsTable
//     rows={result.rows}
//     columns={['position','lotto']}
//     totals={result.totals}          // aggregati SERVER sull'intero set filtrato
//     totalRows={result.total}
//     showPz                           // vedi sotto: solo se il filtro isola un articolo
//     onStorico={row => ...}
//     emptyText="Nessuna giacenza." />

// Descrizione articolo su una seconda riga dentro la stessa cella (non come colonna
// propria): recupera ~200px di larghezza, che sul kiosk sono la differenza fra stare nel
// viewport e avere lo scroll orizzontale.
const COLS = {
    position: { th: 'Posizione', td: (r) => r.position_cella },
    component: {
        th: 'Articolo',
        td: (r) => (
            <>
                <div>{r.component_code}</div>
                {r.component_description && <div className="muted pkg-cell-sub">{r.component_description}</div>}
            </>
        ),
    },
    division: { th: 'Divisione', td: (r) => r.division_name },
    lotto: { th: 'Lotto', td: (r) => r.lotto },
};

// `showPz`: la somma dei PEZZI ha senso fisico solo quando i risultati sono di un articolo
// solo — sommare pezzi di articoli con UM diverse (m, kg, pz) produce un numero che sembra
// un dato ma non lo è. I COLLI invece sono unità contabili omogenee e si sommano sempre.
const StockResultsTable = ({
    rows = [], columns = [], totals = null, totalRows = null, showPz = false,
    onStorico, onPartials, emptyText = 'Nessuna giacenza.',
}) => {
    const cols = columns.filter((k) => COLS[k]);
    const colSpan = cols.length + 4; // 3 colonne quantità + colonna azioni

    return (
        <div className="pkg-table-wrap">
            <table>
                <thead>
                    <tr className="table-head">
                        {cols.map((k) => <th key={k}>{COLS[k].th}</th>)}
                        <th className="text-right">Colli int. × pz/collo</th>
                        <th className="text-right">Colli aperti</th>
                        <th className="text-right">Tot. pz</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => r.__empty ? (
                        // Cella senza giacenza: sta in elenco come le altre (è la risposta a
                        // "cosa dovrebbe esserci qui?"), ma senza numeri e senza Storico —
                        // non esiste nessuna riga di stock da cui ricavarli.
                        <tr key={r.stock_uuid} className="pkg-row-empty">
                            {cols.map((k) => <td key={k}>{k === 'position' ? r.position_cella : ''}</td>)}
                            <td colSpan={3} className="muted" style={{ textAlign: 'center' }}>Cella vuota</td>
                            <td></td>
                        </tr>
                    ) : (
                        <tr key={r.stock_uuid || `${r.position_uuid}-${r.component_uuid}-${r.lotto}-${r.qty_per_collo_whole}`}>
                            {cols.map((k) => <td key={k}>{COLS[k].td(r)}</td>)}
                            {/* La pezzatura fa parte della chiave di riga: due righe possono differire
                                SOLO per questa, quindi va mostrata inline (una colonna in più non entra
                                nel budget del terminale). */}
                            <td className="text-right">
                                {fmtQty(r.qty_colli_whole)} <span className="muted">
                                    {perColloOf(r) > 0
                                        ? `× ${fmtQty(perColloOf(r))} = ${fmtQty(r.qty_pieces_whole)} pz`
                                        : `(${fmtQty(r.qty_pieces_whole)} pz)`}
                                </span>
                            </td>
                            {/* I colli aperti sono AGGREGATI qui: righe separate su una vista di sola
                                lettura si leggono come duplicati. Il dettaglio collo per collo sta
                                dietro il bottone. */}
                            <td className="text-right">
                                {Number(r.partials_count) > 0 ? (
                                    <button className="btn-sm btn-indietro" onClick={() => onPartials(r)}
                                        title="Vedi il dettaglio dei colli aperti">
                                        {fmtQty(r.partials_count)} · {fmtQty(r.partials_pieces)} pz
                                    </button>
                                ) : <span className="muted">—</span>}
                            </td>
                            <td className="text-right"><strong>{fmtQty(r.qty_total)}</strong></td>
                            <td><button className="btn-sm blue_butt" onClick={() => onStorico(r)}>Storico</button></td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr><td colSpan={colSpan} className="muted" style={{ textAlign: 'center', padding: '20px' }}>{emptyText}</td></tr>
                    )}
                </tbody>

                {/* I totali arrivano dal BE e valgono sull'INTERO set filtrato, non sulla pagina
                    visualizzata: vanno etichettati come tali, altrimenti cambiando pagina
                    sembrerebbero sbagliati. */}
                {/* Senza righe di giacenza le somme sarebbero tutti zeri: in modalità posizione
                    capita su ogni cella vuota, e una riga "Totale 0 0 —" è solo rumore. */}
                {totals && rows.length > 0 && totalRows !== 0 && (
                    <tfoot>
                        <tr className="pkg-total-row">
                            <td colSpan={cols.length || 1} style={{ textAlign: 'left' }}>
                                Totale <span className="muted">— tutti i risultati, non solo questa pagina</span>
                                {totalRows !== null && (
                                    // "di giacenza": in modalità posizione l'elenco contiene anche le
                                    // celle vuote, che non contribuiscono a nessuna di queste somme.
                                    <div className="muted pkg-cell-sub">
                                        {fmtQty(totalRows)} {Number(totalRows) === 1 ? 'riga' : 'righe'} di giacenza
                                    </div>
                                )}
                            </td>
                            {/* Somma dei soli COLLI: la pezzatura è un formato per-unità e non si
                                somma, per lo stesso motivo per cui i pezzi si sommano solo con showPz. */}
                            <td className="text-right" title="Somma dei soli colli interi: i pezzi per collo non si sommano">{fmtQty(totals.qty_colli_whole)}</td>
                            <td className="text-right" title="Numero di colli aperti nel set filtrato">{fmtQty(totals.qty_colli_partial)}</td>
                            <td className="text-right">
                                {showPz
                                    ? <strong>{fmtQty(totals.qty_total)}</strong>
                                    : <span className="muted" title="Somma non calcolabile: i risultati contengono articoli con unità di misura diverse">—</span>}
                            </td>
                            <td></td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default StockResultsTable;
